import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material'
import { Loading } from 'components'
import { BreadcrumbItem, Breadcrumbs } from 'components/Breadcrumbs'
import { Typography } from 'components/Typography'
import { useFetchData } from 'hooks/use-fetch-data'
import { useParams } from 'react-router-dom'
import clientProducer from 'services/api/v1/producer/clientProducer'
import { moneyFormatter } from 'utils/formatter'

type TerminalSalesResponse = {
  event: {
    id: number
    name: string
  }
  sectors: {
    id: number
    name: string
    items: {
      id: number
      name: string
      price: string
      quantity: string
      total: string
    }[]
  }[]
  total: string
  terminal: {
    nickname: string
    serialNumber: string
  }
  paymentMethods: {
    cash: string
    credit: string
    debit: string
    pix: string
  }
}

export function TerminalSales() {
  const { eventId, serialNumber } = useParams()

  const queryReport = useFetchData(
    async () =>
      await clientProducer.get<TerminalSalesResponse>(
        `/events/${eventId}/reports/pos/${serialNumber}`,
      ),
    {
      enabled: !!eventId && !!serialNumber,
    },
  )

  if (queryReport.isLoading) return <Loading />

  if (queryReport.isError || !queryReport.data) {
    console.error(queryReport.error)
    return <Typography color="error">Erro ao carregar relatório</Typography>
  }

  const { data: report } = queryReport.data

  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Eventos', to: '/organizador/eventos' },
    {
      title: report.event.name,
      to: `/organizador/eventos/${report.event.id}`,
    },
    { title: 'Relatório por PDV' },
    { title: report.terminal.serialNumber },
  ]

  return (
    <Grid container spacing={2} p={2} component={Paper}>
      <Grid item xs={12}>
        <Breadcrumbs items={breadcrumbs} />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" component="p">
          {serialNumber} - {report.terminal.nickname}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Forma de Pagamento</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Dinheiro</TableCell>
              <TableCell align="right">
                {moneyFormatter.format(Number(report.paymentMethods.cash))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Cartão de Crédito</TableCell>
              <TableCell align="right">
                {moneyFormatter.format(Number(report.paymentMethods.credit))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Cartão de Débito</TableCell>
              <TableCell align="right">
                {moneyFormatter.format(Number(report.paymentMethods.debit))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Pix</TableCell>
              <TableCell align="right">
                {moneyFormatter.format(Number(report.paymentMethods.pix))}
              </TableCell>
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell>Total Geral:</TableCell>
              <TableCell align="right">
                {moneyFormatter.format(Number(report.total))}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </Grid>
      <Grid item xs={12}>
        {report.sectors.map((sector) => (
          <Table key={sector.id} sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="h6">{sector.name}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Preço</TableCell>
                <TableCell>Quantidade</TableCell>
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sector.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    {moneyFormatter.format(Number(item.price))}
                  </TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>
                    {moneyFormatter.format(Number(item.total))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={3} align="right">
                  Total:
                </TableCell>
                <TableCell>
                  {moneyFormatter.format(
                    sector.items.reduce(
                      (acc, item) => acc + Number(item.total),
                      0,
                    ),
                  )}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        ))}
      </Grid>
    </Grid>
  )
}
